import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Form,
  Button,
  Input,
  message,
  Select,
  Table,
  DatePicker,
  Tabs,
  InputNumber,
  Switch,
  Tooltip,
  Modal,
} from "antd";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageHeader from "../Layout/PageHeader";
import AnnouncementFilters from "./AnnouncementFilters";
import { CaretRightOutlined } from "@ant-design/icons";
import { formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const Announcement = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [announcements, setAnnouncements] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [form] = Form.useForm();

  // Quill modules configuration for toolbar
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      ["link", "clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
  ];

  const fetchAnnouncements = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));
    try {
      const params = {
        ...filters,
        limit: pagination.limit,
        page: pagination.page,
      };
      const res = await axios.get("/api/announcements", { params });
      setAnnouncements(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filters]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    const languages = ["en", "ms", "zh"];

    // Common fields
    const commonData = {
      type: values.type,
      startDate: values.dateRange ? values.dateRange[0].toISOString() : null,
      endDate: values.dateRange ? values.dateRange[1].toISOString() : null,
      doNotShowDays: values.doNotShowDays,
    };

    const missingLanguages = [];
    for (const lang of languages) {
      const titleKey = `title_${lang}`;
      const textKey = `text_${lang}`;

      if (!values[titleKey] || !values[textKey]) {
        const langName = lang === "en" ? "English" : lang === "ms" ? "Malay" : "Chinese";
        missingLanguages.push(langName);
      }
    }

    if (missingLanguages.length > 0) {
      message.error(`All 3 languages are required. Missing: ${missingLanguages.join(", ")}`);
      setLoading((prev) => ({ ...prev, create: false }));
      return;
    }

    const announcementsToCreate = [];
    for (const lang of languages) {
      const titleKey = `title_${lang}`;
      const textKey = `text_${lang}`;

      announcementsToCreate.push({
        ...commonData,
        title: values[titleKey],
        text: values[textKey],
        lang: lang,
      });
    }

    try {
      await axios.post("/api/announcements/bulk", {
        announcements: announcementsToCreate,
      });
      message.success(`Successfully created ${announcementsToCreate.length} announcement(s).`);
      form.resetFields();
      fetchAnnouncements();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }

    setLoading((prev) => ({ ...prev, create: false }));
  };

  const updateHandler = async (row, changes) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      await axios.put(`/api/announcements/${row.objectId}`, changes);
      message.success("Announcement updated");
      fetchAnnouncements();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      width: 200,
      render: (_, record) => {
        return permissions.includes(PERMISSIONS.UPDATE_ANNOUCEMENT) ? (
          <Input
            key={record.objectId}
            defaultValue={record.title}
            onBlur={(e) => {
              if (e.target.value !== record.title) {
                updateHandler(record, { title: e.target.value });
              }
            }}
          />
        ) : (
          record.title
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 100,
    },
    {
      title: "Lang",
      dataIndex: "lang",
      width: 80,
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      width: 150,
      render: (date) => (date ? formatDateTime(date.iso || date) : "-"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      width: 150,
      render: (date) => (date ? formatDateTime(date.iso || date) : "-"),
    },
    {
      title: "Active",
      dataIndex: "isAnnouncement",
      width: 100,
      render: (_, record) =>
        permissions.includes(PERMISSIONS.UPDATE_ANNOUCEMENT) ? (
          <Switch
            checked={record.isAnnouncement}
            onChange={(checked) => updateHandler(record, { isAnnouncement: checked })}
          />
        ) : record.isAnnouncement ? (
          "Yes"
        ) : (
          "No"
        ),
    },
    {
      title: "Text",
      dataIndex: "text",
      ellipsis: true,
      render: (_, record) =>
        permissions.includes(PERMISSIONS.UPDATE_ANNOUCEMENT) ? (
          <Tooltip title="Click to edit HTML in rich text editor.">
            <Button
              size="small"
              onClick={() => {
                let content = record.text;
                Modal.info({
                  title: "Edit Content",
                  width: 800,
                  content: (
                    <div style={{ height: "400px" }}>
                      <ReactQuill
                        theme="snow"
                        defaultValue={content}
                        modules={quillModules}
                        formats={quillFormats}
                        onChange={(value) => {
                          content = value;
                        }}
                        style={{ height: "300px" }}
                      />
                      <div style={{ marginTop: "50px", float: "right" }}>
                        <Button onClick={() => Modal.destroyAll()} style={{ marginRight: 8 }}>
                          Close
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => {
                            updateHandler(record, { text: content });
                            Modal.destroyAll();
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ),
                  okButtonProps: { style: { display: "none" } },
                });
              }}
            >
              Edit Content
            </Button>
          </Tooltip>
        ) : (
          "View Content"
        ),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      width: 150,
      render: (date) => (date ? formatDateTime(date.iso || date) : "-"),
    },
  ];

  return (
    <div>
      <PageHeader title="Announcement" childRoutes={[{ path: "/announcement", breadcrumbName: "Announcement" }]} />

      {permissions.includes(PERMISSIONS.CREATE_ANNOUCEMENT) && (
        <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
          <Collapse.Panel header="Create New Announcement" key="create-announcement">
            <Form form={form} layout="vertical" onFinish={createHandler}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                    <Input placeholder="e.g. System, Marketing" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="dateRange" label="Start & End Date">
                    <RangePicker showTime />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="doNotShowDays" label="Do Not Show Days (number)">
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Tabs defaultActiveKey="en" type="card">
                <TabPane tab="English" key="en">
                  <Form.Item name="title_en" label="Title (EN)">
                    <Input />
                  </Form.Item>
                  <Form.Item name="text_en" label="Content (EN)">
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: "300px", marginBottom: "50px" }}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="Malay" key="ms">
                  <Form.Item name="title_ms" label="Title (MS)">
                    <Input />
                  </Form.Item>
                  <Form.Item name="text_ms" label="Content (MS)">
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: "300px", marginBottom: "50px" }}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="Chinese" key="zh">
                  <Form.Item name="title_zh" label="Title (ZH)">
                    <Input />
                  </Form.Item>
                  <Form.Item name="text_zh" label="Content (ZH)">
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ height: "300px", marginBottom: "50px" }}
                    />
                  </Form.Item>
                </TabPane>
              </Tabs>

              <Button type="primary" htmlType="submit" loading={loading.create}>
                Create
              </Button>
            </Form>
          </Collapse.Panel>
        </Collapse>
      )}

      <Collapse
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        style={{ marginTop: 16 }}
      >
        <Collapse.Panel header="Filters" key="filters">
          <AnnouncementFilters filters={filters} onFiltersChange={setFilters} />
        </Collapse.Panel>
      </Collapse>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={announcements}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit || 50,
          total: pagination.total,
          onChange: (page, pageSize) => setPagination({ ...pagination, page: page, limit: pageSize }),
        }}
        loading={loading.index || loading.update}
        scroll={{ x: 1200 }}
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

export default Announcement;
